import React, { useRef, useState } from "react";
import { api, formatApiError, fmtINR } from "../lib/apiClient";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import { FieldStyle } from "./PartiesPage";
import { UploadSimple, ArrowsClockwise, CheckCircle } from "@phosphor-icons/react";

export default function TallyImportPage() {
  const fileRef = useRef(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [session, setSession] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async (file) => {
    if (!file) return;
    setAnalyzing(true); setSession(null); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/tally/import/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      setSession(data);
      toast.success(`Parsed ${data.entries?.length || 0} Tally entries`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
    finally { setAnalyzing(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const confirm = async () => {
    setConfirming(true);
    try {
      const { data } = await api.post("/tally/import/confirm", { session_id: session.id });
      setResult(data);
      toast.success(`Imported ${data.imported_vouchers} vouchers, ${data.payments_recorded} payments matched`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setConfirming(false); }
  };

  return (
    <div data-testid="tally-import-page">
      <PageHeader title="Tally Data Import (AI)" subtitle="Upload your Tally Prime export (XML, Excel or CSV). AI reads old receipts/payments, posts them to ledgers and matches receipts to open invoices." />
      <div className="p-8 max-w-5xl space-y-6">
        <div className="card-base p-5 flex items-center gap-4 flex-wrap">
          <input ref={fileRef} type="file" accept=".xml,.xlsx,.xls,.csv" className="hidden" data-testid="tally-file-input" onChange={e => upload(e.target.files?.[0])} />
          <button onClick={() => fileRef.current?.click()} disabled={analyzing} className="btn-primary inline-flex items-center gap-1.5" data-testid="tally-upload-btn">
            {analyzing ? <ArrowsClockwise size={14} className="animate-spin" weight="bold" /> : <UploadSimple size={14} weight="bold" />}
            {analyzing ? "AI reading Tally export…" : "Upload Tally Export"}
          </button>
          <div className="text-xs text-ink-muted">In Tally Prime: Day Book → Alt+E (Export) → XML or Excel</div>
        </div>

        {session && !result && (
          <div className="card-base p-0 overflow-hidden" data-testid="tally-review">
            <div className="flex items-center justify-between px-5 py-3 border-b border-border">
              <div className="font-display font-semibold">{session.filename} — {session.entries.length} entries</div>
              <button onClick={confirm} disabled={confirming || session.entries.length === 0} className="btn-primary inline-flex items-center gap-1.5" data-testid="tally-confirm-btn">
                <CheckCircle size={14} weight="bold" /> {confirming ? "Importing…" : "Import All"}
              </button>
            </div>
            <div className="table-wrapper border-0 rounded-none">
              <table className="table-base">
                <thead><tr><th>Date</th><th>Type</th><th>Party / Narration</th><th>Dr → Cr</th><th className="text-right">Amount</th><th>Invoice Match</th></tr></thead>
                <tbody>
                  {session.entries.map((e, i) => (
                    <tr key={e.id || `${e.date}-${e.narration}-${i}`} data-testid={`tally-row-${i}`}>
                      <td className="tabular text-xs">{e.date}</td>
                      <td><span className="status-pill status-neutral">{e.voucher_type}</span></td>
                      <td className="text-xs">{e.party}<div className="text-[10px] text-ink-muted">{e.narration}</div></td>
                      <td className="text-xs">{e.dr_ledger} → {e.cr_ledger}</td>
                      <td className="text-right tabular font-semibold">{fmtINR(e.amount)}</td>
                      <td className="text-xs">{e.invoice_match ? <span className="text-[#1C4F32] font-semibold">✓ {e.invoice_match.number}</span> : <span className="text-ink-muted">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {result && (
          <div className="card-base p-5 text-sm" data-testid="tally-result">
            ✓ Imported <b>{result.imported_vouchers}</b> vouchers into the Day Book · <b>{result.payments_recorded}</b> receipt(s) matched to invoices and recorded as payments.
          </div>
        )}
      </div>
      <FieldStyle />
    </div>
  );
}
